import {
  DEFAULT_TERMINAL_ID,
  type TerminalClearInput,
  type TerminalCloseInput,
  type TerminalEvent,
  type TerminalOpenInput,
  type TerminalResizeInput,
  type TerminalRestartInput,
  type TerminalSessionSnapshot,
  type TerminalSignalInput,
  type TerminalWriteInput,
  WS_METHODS,
} from "@cmmd-forge/contracts";
import { createTerminalControlSequencer } from "@cmmd-forge/client-runtime";

import { isTransportConnectionErrorMessage } from "./transportError";
import type { WsTransport } from "./wsTransport";

export interface TerminalRpcClient {
  readonly open: (input: TerminalOpenInput) => Promise<TerminalSessionSnapshot>;
  readonly write: (input: TerminalWriteInput) => Promise<void>;
  readonly resize: (input: TerminalResizeInput) => Promise<void>;
  readonly signal: (input: TerminalSignalInput) => Promise<void>;
  readonly clear: (input: TerminalClearInput) => Promise<void>;
  readonly restart: (input: TerminalRestartInput) => Promise<TerminalSessionSnapshot>;
  readonly close: (input: TerminalCloseInput) => Promise<void>;
  readonly onEvent: (listener: (event: TerminalEvent) => void) => () => void;
}

const MAX_PENDING_TERMINAL_WRITE_BYTES = 256 * 1024;
const RETRY_DELAY_MS = 250;

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

export function createTerminalControlClient(transport: WsTransport): {
  readonly terminal: TerminalRpcClient;
  readonly dispose: () => void;
} {
  const controllerId = crypto.randomUUID();
  const sequencer = createTerminalControlSequencer({
    controllerId,
    maxPendingWriteBytes: MAX_PENDING_TERMINAL_WRITE_BYTES,
  });
  let disposed = false;

  const registerSnapshot = (snapshot: TerminalSessionSnapshot): void => {
    if (!snapshot.sessionEpoch) {
      throw new Error("Connected Forge runtime does not support epoch-fenced terminal controls.");
    }
    sequencer.register({
      threadId: snapshot.threadId,
      terminalId: snapshot.terminalId,
      sessionEpoch: snapshot.sessionEpoch,
    });
  };

  const requestUntilAcknowledged = async <T>(request: () => Promise<T>): Promise<T> => {
    for (;;) {
      if (disposed) break;
      try {
        return await request();
      } catch (error) {
        if (!isTransportConnectionErrorMessage(errorMessage(error))) throw error;
        await delay(RETRY_DELAY_MS);
      }
    }
    throw new Error("Terminal control client was disposed before acknowledgement.");
  };

  const terminal: TerminalRpcClient = {
    open: async (input) => {
      const snapshot = await transport.request((client) =>
        client[WS_METHODS.terminalOpen]({
          ...input,
          terminalId: input.terminalId ?? DEFAULT_TERMINAL_ID,
        }),
      );
      registerSnapshot(snapshot);
      return snapshot;
    },
    write: async (input) => {
      const terminalId = input.terminalId ?? DEFAULT_TERMINAL_ID;
      await sequencer.enqueue({
        threadId: input.threadId,
        terminalId,
        writeBytes: new TextEncoder().encode(input.data).byteLength,
        execute: (identity) =>
          requestUntilAcknowledged(() =>
            transport.request((client) =>
              client[WS_METHODS.terminalWrite]({
                ...input,
                terminalId,
                ...identity,
              }),
            ),
          ),
      });
    },
    resize: async (input) => {
      const terminalId = input.terminalId ?? DEFAULT_TERMINAL_ID;
      await sequencer.enqueue({
        threadId: input.threadId,
        terminalId,
        execute: (identity) =>
          requestUntilAcknowledged(() =>
            transport.request((client) =>
              client[WS_METHODS.terminalResize]({ ...input, terminalId, ...identity }),
            ),
          ),
      });
    },
    signal: async (input) => {
      const terminalId = input.terminalId ?? DEFAULT_TERMINAL_ID;
      await sequencer.enqueue({
        threadId: input.threadId,
        terminalId,
        execute: (identity) =>
          requestUntilAcknowledged(() =>
            transport.request((client) =>
              client[WS_METHODS.terminalSignal]({ ...input, terminalId, ...identity }),
            ),
          ),
      });
    },
    clear: async (input) => {
      const terminalId = input.terminalId ?? DEFAULT_TERMINAL_ID;
      await sequencer.enqueue({
        threadId: input.threadId,
        terminalId,
        execute: (identity) =>
          requestUntilAcknowledged(() =>
            transport.request((client) =>
              client[WS_METHODS.terminalClear]({ ...input, terminalId, ...identity }),
            ),
          ),
      });
    },
    restart: async (input) => {
      const terminalId = input.terminalId ?? DEFAULT_TERMINAL_ID;
      let snapshot: TerminalSessionSnapshot | null = null;
      await sequencer.enqueue({
        threadId: input.threadId,
        terminalId,
        execute: async (identity) => {
          const result = await requestUntilAcknowledged(() =>
            transport.request((client) =>
              client[WS_METHODS.terminalRestart]({ ...input, terminalId, ...identity }),
            ),
          );
          snapshot = result.snapshot;
        },
      });
      if (snapshot === null) throw new Error("Terminal restart completed without a snapshot.");
      registerSnapshot(snapshot);
      return snapshot;
    },
    close: async (input) => {
      const terminalIds = input.terminalId
        ? [input.terminalId]
        : sequencer.terminalIds(input.threadId);
      for (const terminalId of terminalIds) {
        await sequencer.enqueue({
          threadId: input.threadId,
          terminalId,
          execute: (identity) =>
            requestUntilAcknowledged(() =>
              transport.request((client) =>
                client[WS_METHODS.terminalClose]({ ...input, terminalId, ...identity }),
              ),
            ),
        });
        sequencer.remove(input.threadId, terminalId);
      }
    },
    onEvent: (listener) =>
      transport.subscribe(
        (client) => client[WS_METHODS.subscribeTerminalEvents]({}),
        (event) => {
          if (event.type === "started" || event.type === "restarted") {
            registerSnapshot(event.snapshot);
          }
          listener(event);
        },
      ),
  };

  return {
    terminal,
    dispose: () => {
      disposed = true;
      sequencer.dispose();
    },
  };
}
